import { useState } from "react";
import { useOutsideClick } from "src/Hooks";
import { EQActionType, EQStateType } from "src/Views/EQueue";
import style from "./ReturnWidget.module.scss";
import classNames from "classnames";
import { Select2WebElementType } from "src/Types/ReDoc";
import { ReactComponent as TimerReTurnIcon } from '../../Assets/ReTurnQueue/Timer.svg';
import { ReactComponent as InfoReTurnIcon } from '../../Assets/ReTurnQueue/infoReTurn.svg';
import { ReactComponent as QueueWaitingIcon } from '../../Assets/ReTurnQueue/QueueWaiting.svg';
import { ReactComponent as QueueDeferredIcon } from '../../Assets/ReTurnQueue/QueueDeferred.svg';
import { ReactComponent as QueueRedirectedIcon } from '../../Assets/ReTurnQueue/QueueRedirected.svg';

export type ReturnWidgetDisplayMode = "queue" | "ticket" | "pause";

interface IReturnWidgetProps {
	// Состояние виджета
	state: EQStateType;
	displayMode?: ReturnWidgetDisplayMode;
	userName?: string;
	// Функция вызывается при клике на кнопки справа
	onAction: (action: string, payload?: unknown) => void;
	// Функция вызывается при закрытии виджета
	onClose: () => void;
	// Номер талона
	ticketNumber?: string;
	// Описание талона
	ticketDescription?: string;
	ticketCustomerName?: string;
	// Количество людей в очереди
	queueCount: number;
	deferredCount?: number;
	transferedCount?: number;
	// Окна для перенаправления посетителя
	windows?: Select2WebElementType[];
	// Статус загрузки окон для перенаправления посетителя
	windowsStatus?: "idle" | "loading" | "loaded";
	windowsError?: string;
	isWaiting?: boolean;
	// Кнопки действия состояния
	actions?: EQActionType[];
	restTime?: string | boolean;
	comment?: string;
}

const getFirstName = (fullName?: string): string => {
	const nameParts = fullName?.trim().split(/\s+/).filter(Boolean) || [];
	return nameParts.length > 1 ? nameParts[1] : (nameParts[0] || "");
};

const ReturnWidget: React.FC<IReturnWidgetProps> = ({
	state,
	displayMode = "queue",
	userName,
	onAction,
	onClose,
	ticketNumber,
	ticketDescription,
	ticketCustomerName,
	queueCount,
	deferredCount = 0,
	transferedCount = 0,
	windows,
	windowsStatus = "idle",
	windowsError,
	isWaiting = false,
	restTime,
	actions,
	comment
}) => {
	const widgetRef = useOutsideClick<HTMLDivElement>(onClose);
	const [ isAutoCall, setIsAutoCall ] = useState<boolean>(true);
	const [ expandedAction, setExpandedAction ] = useState<string | null>(null);
	const [ showRedirectList, setShowRedirectList ] = useState<boolean>(false);
	const [ showOpenWindowList, setShowOpenWindowList ] = useState<boolean>(false);

	const isTicketMode = displayMode === "ticket";
	const isQueueMode = displayMode === "queue";
	const title = isTicketMode ? (ticketNumber || "Талон не определён") : (ticketNumber || `${queueCount} человек`);
	const description = ticketDescription || (isQueueMode ? "В очереди" : "");
	const showAutoCall = !state.hideAutoCall;
	const showQueueInfo = !state.hideQueueInfo;
	const firstName = getFirstName(userName);
	const introText = state.introText && firstName
		? `Здравствуйте, ${firstName}\nХорошего рабочего дня!`
		: state.introText;
	const showIntro = !!introText;
	const showQueueStats = isQueueMode && !!state.showQueueStats;
	const isCompactPause = !!state.small && !actions?.some((action) => action.name === "schedule");
	// Окна для перенаправления посетителя
	const windowOptions = (windows || []).map((w) => ({ id: w.id, title: w.text || w.id }));

	const queueStats = [
		{ value: queueCount, label: "В очереди", icon: QueueWaitingIcon },
		...(deferredCount > 0
			? [{ value: deferredCount, label: "Отложены", icon: QueueDeferredIcon }]
			: []),
		...(transferedCount > 0
			? [{ value: transferedCount, label: "Перенаправлены", icon: QueueRedirectedIcon }]
			: []),
	];
	const hasExpandedQueueStats = showQueueStats && queueStats.length > 2;

	const closeWindowLists = () => {
		setShowRedirectList(false);
		setShowOpenWindowList(false);
	};

	const renderEmptyWindowsMessage = () => {
		return (
			<div className={classNames("body-xsmall", style.returnEqWidgetDropdownItem, style.returnEqWidgetDropdownMessage)}>
				{windowsError || "Нет доступных окон"}
			</div>
		);
	};
	
	/** Вызывается при клике на кнопки справа */
	const onActionClick = ( actionName: string ) => {
		if ( actionName === "return-redirect" ) {
			onAction( actionName );
			setShowOpenWindowList(false);
			setShowRedirectList( prev => !prev );
			return;
		}
		if ( actionName === "return-open-window" ) {
			onAction( actionName );
			setShowRedirectList(false);
			setShowOpenWindowList(prev => !prev);
			return;
		}
		setShowRedirectList(false);
		setShowOpenWindowList(false);
		onAction( actionName );
	};

	/** Вызывается при клике на элемент дропдауна в кнопках справа */
	const onSubActionClick = ( actionName: string ) => {
		setExpandedAction(null);
		setShowRedirectList(false);
		setShowOpenWindowList(false);
		onAction(actionName);
	};

	return (
		<div
			ref={widgetRef}
			onClick={closeWindowLists}
			className={classNames(style.returnEqWidget, {
				[style.returnEqWidgetWide]: state.wide,
				[style.returnEqWidgetSmall]: state.small,
				[style.returnEqWidgetPauseCompact]: isCompactPause,
				[style.returnEqWidgetMedium]: state.medium,
				[style.returnEqWidgetCompact]: state.compact,
				[style.returnEqWidgetStatsExpanded]: hasExpandedQueueStats,
				[style.returnEqWidgetIntroMode]: showIntro
			})}
			data-return-widget="true"
		>
			<div
				className={classNames(style.returnEqWidgetHeader, {
					[style.returnEqWidgetHeaderRight]: !showQueueInfo && !showIntro
				})}
				style={{ borderTop: `12px solid ${state.borderTopColor || state.color}` }}
			>
				{/* бабл виджета со статусом */}
				<h4
					className={classNames("body-small", style.returnEqWidgetHeaderTitle)}
					style={{
						color: state.borderTopColor || state.color,
						background: state.badgeBackgroundColor
					}}
				>
					{state.text || "re:turn"}
				</h4>
				{showAutoCall && (
					<div className={style.returnEqWidgetSwitchRow}>
						<span className={classNames("body-xsmall", style.returnEqWidgetSwitchLabel)}>Автовызов</span>
						<button
							type="button"
							role="switch"
							aria-checked={isAutoCall}
							className={classNames(style.returnEqWidgetSwitch, {
								[ style.returnEqWidgetSwitchChecked ]: isAutoCall
							})}
							onClick={() => setIsAutoCall((prev) => !prev)}
						>
							<span className={style.returnEqWidgetSwitchThumb} />
						</button>
					</div>
				)}
			</div>

			<div className={classNames(style.returnEqWidgetBody, {
				[style.returnEqWidgetBodyWithoutQueue]: !showQueueInfo,
				[style.returnEqWidgetBodyIntro]: showIntro
			})}>
				{showIntro && (
					<div className={style.returnEqWidgetIntro}>
						{introText}
					</div>
				)}
				{showQueueInfo && !showIntro && (
					<div className={classNames(style.returnEqWidgetBodyLeft, {
						[style.returnEqWidgetBodyLeftStats]: showQueueStats
					})}> 
						{showQueueStats ? (
							<div className={style.returnEqWidgetStats}>
								{queueStats.map((item) => {
									const StatIcon = item.icon;
									return (
										<div key={item.label} className={style.returnEqWidgetStatsItem}>
											<span className={style.returnEqWidgetStatsValue}>{item.value}</span>
											<span className={style.returnEqWidgetStatsIcon}>
												<StatIcon />
											</span>
											<span className={style.returnEqWidgetStatsLabel}>{item.label}</span>
										</div>
									);
								})}
							</div>
						) : (
							<>
								<div className={style.returnEqWidgetBodyLeftMain}>
									<h3 className={classNames("body-large", style.returnEqWidgetBodyLeftTitle)}>{title}</h3>
									{restTime && <div className={classNames('body-xsmall', style.returnEqWidgetBodyLeftTimer)}>
										<TimerReTurnIcon/>
										{restTime}
									</div>}
								</div>
								{!!ticketCustomerName && <p className={classNames("body-xsmall", style.returnEqWidgetBodyLeftCustomer)}>{ticketCustomerName}</p>}
								{!!ticketDescription && (
									<p className={classNames("body-xsmall", style.returnEqWidgetBodyLeftDesc)}>
										{ticketDescription}
									</p>
								)}
								{!ticketDescription && !!description && (
									<p className={classNames(!ticketNumber ? style.returnEqWidgetBodyLeftDescBig : "body-xsmall", style.returnEqWidgetBodyLeftDesc)}>{description}</p>
								)}
								{ comment && <div className={classNames("body-xsmall", style.returnEqWidgetBodyLeftComment)}>
									<InfoReTurnIcon/>
									<p>{comment}</p>
								</div>}
							</>
						)}
					</div>
				)}
				<div className={style.returnEqWidgetBodyRight}>
					{(actions || []).map((action, index) => {
						const ActionIcon = action.icon;
						const subActions = action.subAction ?? [];
						const hasSubMenu = subActions.length > 1;
						return (
						<div key={action.name} className={style.returnEqWidgetActionContainer}>
							{/* Основная кнопка действия состояния */}
							<button
								type="button"
								disabled={action.disabled || isWaiting}
								className={classNames(
									"body-xsmall",
									style.returnEqWidgetAction,
									index === 0 ? style.returnEqWidgetActionPrimary : style.returnEqWidgetActionSecondary,
									{
										[style.returnEqWidgetActionWithArrow]: index === 0 && hasSubMenu,
										[style.returnEqWidgetActionExpanded]: expandedAction === action.name
									}
								)}
								onClick={(event) => {
									if (action.name === "return-redirect" || action.name === "return-open-window") {
										event.stopPropagation();
									}
									if (hasSubMenu) {
										setExpandedAction(prev => prev === action.name ? null : action.name);
										setShowRedirectList(false);
										return;
									}
									onActionClick(action.name);
								}}
							>
								{
									!!ActionIcon &&
									<span className={style.returnEqWidgetActionIcon}>
										<ActionIcon />
									</span>
								}
								<span className={style.returnEqWidgetActionText}>{action.title || action.name}</span>
								{
									index === 0 && hasSubMenu &&
									<span className={classNames(
										style.returnEqWidgetActionArrow,
										expandedAction === action.name && style.returnEqWidgetActionArrowOpen
									)}>
										<svg xmlns="http://www.w3.org/2000/svg" width="9" height="5" viewBox="0 0 9 5" fill="none">
										<path d="M0.000650406 -3.64262e-07L4.16732 4.16667L8.33398 0L0.000650406 -3.64262e-07Z" fill="white"/>
										</svg>
									</span>
								}
							</button>
							{/* Выпадающий список поддействий (например, "В порядке очереди" или "По номеру талона") */}
							{
								expandedAction === action.name &&
								hasSubMenu &&
								<div className={style.returnEqWidgetDropdown}>
									{
										subActions.map( subAction => {
											const SubActionIcon = subAction.icon;
											return (
											<button
												key={subAction.name}
												type="button"
												className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}
												onClick={() => onSubActionClick(subAction.name)}
											>
												{
													!!SubActionIcon &&
													<span className={style.returnEqWidgetActionIcon}>
														<SubActionIcon />
													</span>
												}
												<span>{subAction.title || subAction.name}</span>
											</button>
											);
										})
									}
								</div>
							}

							{/* Выпадающий список окон для перенаправления посетителя */}
							{
								action.name === "return-redirect" && showRedirectList &&
								<div className={style.returnEqWidgetDropdown} onClick={(event) => event.stopPropagation()}>
									{
										windowsStatus !== "loaded" &&
										<div className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}>Загрузка...</div>
									}
									{
										windowsStatus === "loaded" &&
										windowOptions.map( (windowItem) => (
											<button
												key={windowItem.id}
												type="button"
												className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}
												onClick={() => {
													setShowRedirectList(false);
													onAction("return-redirect-select", windowItem.id);
												}}
											>
												{windowItem.title}
											</button>
										))
									}
									{
										windowsStatus === "loaded" && windowOptions.length === 0 &&
										renderEmptyWindowsMessage()
									}
								</div>
							}
							{
								action.name === "return-open-window" && showOpenWindowList &&
								<div className={style.returnEqWidgetDropdown} onClick={(event) => event.stopPropagation()}>
									{
										windowsStatus !== "loaded" &&
										<div className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}>Загрузка...</div>
									}
									{
										windowsStatus === "loaded" &&
										windowOptions.map( (windowItem) => (
											<button
												key={windowItem.id}
												type="button"
												disabled={isWaiting}
												className={classNames("body-xsmall", style.returnEqWidgetDropdownItem)}
												onClick={() => {
													setShowOpenWindowList(false);
													onAction("return-open-window-select", windowItem.id);
												}}
											>
												{windowItem.title}
											</button>
										))
									}
									{
										windowsStatus === "loaded" && windowOptions.length === 0 &&
										renderEmptyWindowsMessage()
									}
								</div>
							}
						</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default ReturnWidget;
